import React, { useEffect, useState, type ReactNode } from 'react';

type ExpandableCardProps = {
  /** Heading content that is always visible (text or custom JSX). */
  heading: ReactNode;
  /** Start opened? */
  defaultOpen?: boolean;
  /** Optional className to style the outer card. */
  className?: string;
  /** Called whenever open state changes. */
  onToggle?(open: boolean): void;
  /** Body content shown when expanded. */
  setExpanded?(expanded: boolean): void;
  /**
   * allows all expanded or all closed
   */
  children: ReactNode;
};

export const ExpandableCard: React.FC<ExpandableCardProps> = ({
  heading,
  defaultOpen = false,
  className = '',
  onToggle,
  setExpanded,
  children
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  };

  return (
    <section
      className={[
        'w-full rounded-2xl border',
        'border-zinc-200  shadow-sm',
        'backdrop-blur-sm',
        className
      ].join(' ')}
    >
      {/* Header (always visible) */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={[
          'group w-full flex items-center justify-between gap-3',
          'px-4 py-3 sm:px-5 sm:py-4',
          'text-left select-none',
          'hover:bg-[#56dac5]',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60',
          'rounded-2xl'
        ].join(' ')}
      >
        <div className="flex items-center gap-3">
          {/* Accent dot */}
          <span
            className={[
              'inline-block h-2.5 w-2.5 rounded-full',
              open ? 'bg-blue-600' : 'bg-zinc-300 '
            ].join(' ')}
            aria-hidden="true"
          />
          <div className="text-base sm:text-lg font-semibold text-zinc-900">
            {heading}
          </div>
        </div>

        {/* Chevron */}
        <svg
          className={[
            'h-5 w-5 shrink-0 transition-transform duration-300 ease-out',
            open ? 'rotate-180' : 'rotate-0',
            'text-zinc-500 group-hover:text-zinc-700'
          ].join(' ')}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.13l3.71-2.9a.75.75 0 011.04 1.08l-4.24 3.31a.75.75 0 01-.93 0L5.21 8.31a.75.75 0 01.02-1.1z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Animated content: grid trick for smooth height transition */}
      <div
        className={[
          'grid transition-[grid-template-rows] duration-300 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        ].join(' ')}
        aria-hidden={!open}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 sm:px-5 sm:pb-5 text-sm sm:text-[15px] rounded-xl leading-relaxed text-zinc-700 bg-white" >
            {children}
          </div>
        </div>
      </div>
    </section>
  );
};
